import { FC, useEffect } from "react";

import {
  SiteBody,
  SiteCredentials,
  SiteItem,
} from "@vviinn/user-managment-client-ts";
import { PromiseDefaultApi } from "@vviinn/user-managment-client-ts/dist/types/PromiseAPI";

import { useSiteDetails } from "../../app/hooks/useSiteDetails";

import {
  deleteSite,
  upsertSite,
  resetCredentials,
  resetToken,
  getCompany,
} from "../../store";

import { trackingConfig } from "../../site";

import { Restricted } from "../../components/Restricted";

import { responseNotification } from "../../components/Notifications";
import { useRequestHandlers } from "../../app/hooks/useRequestHandlers";
import { DetailedWrapper } from "../../ui/layout/general/DetailedWrapper/DetailedWrapper";
import { FeedEditBlock } from "../../site/details/feed/FeedEditBlock";
import { NamedSwitch } from "../../ui/layout/molecules/NamedSwitch";
import { SlideOverFieldNamedRow } from "../../ui/layout/organisms/SlideOver/SlideOverFixedLayouts/SlideOverSingleField/SlideOverFieldNamedRow";
import { AIMethodsBlock } from "../../site/components/AI/AIMethodsBlock";
import { CDNBlock } from "../../site/components/CDN/CDNBlock";
import { RequestInputData } from "../../site/details/types";
import { CredentialsEditBlock } from "../../site/components/Credentials/CredentialsEditBlock";
import { CompanyAndSiteIds } from "../types";
import { handleFeed } from "../../feed/handleFeed";
import { handleProduct } from "../../product/handleProduct";
import { useOutletContext } from "react-router";
import { SubSectionWithStyles } from "../../ui/layout";

const RequestsWithSiteId = (
  requestType: string,
  api: PromiseDefaultApi,
  value: { companyId: string; siteId: number },
) => {
  const siteActions: Record<string, any> = {
    "API credentials": resetCredentials({ api, value }),
    "read only token": resetToken({ api, value }),
  };
  return siteActions[requestType];
};

export const SiteDetails: FC<React.PropsWithChildren<{}>> = () => {
  const { companyId, siteId } = useOutletContext<CompanyAndSiteIds>();
  const handlerParams = useRequestHandlers();
  const { handleRefreshFeed } = handleFeed(handlerParams, companyId, siteId);
  const { getProducts } = handleProduct(handlerParams, siteId, companyId);
  const { getApi, notify, dispatch } = handlerParams;

  let { siteDetails, setSiteDetails } = useSiteDetails();
  const trackingConfiguration = siteDetails?.trackingConfiguration;

  const backLink = `/company/${companyId}/sites`;

  useEffect(() => {
    handleRefreshFeed();
    getProducts();
  }, [siteId]);

  const handleRequests = async (requestData: RequestInputData) => {
    if (!siteDetails) return false;
    const companyId = siteDetails.companyId;
    if (!companyId) return false;
    notify("Info", `Updating ${requestData.requestType}`);
    const api = await getApi();
    let response;
    if (requestData.siteBody !== undefined) {
      response = await dispatch(
        upsertSite({
          api,
          value: { companyId, siteBody: requestData.siteBody },
        }),
      );
      if (response.payload) {
        setSiteDetails(response.payload as SiteItem);
      }
    } else {
      const siteId = siteDetails.id;
      response = await dispatch(
        RequestsWithSiteId(requestData.requestType, api, { companyId, siteId }),
      );
      const credentials = response.payload.credentials;
      let newCredentials: boolean | SiteCredentials = false;
      if (credentials.api) {
        newCredentials = { ...siteDetails.credentials, api: credentials.api };
      } else if (credentials.jwt) {
        newCredentials = { ...siteDetails.credentials, jwt: credentials.jwt };
      }
      if (newCredentials)
        setSiteDetails({ ...siteDetails, credentials: newCredentials });
    }
    return responseNotification(
      notify,
      !!response.payload,
      requestData.requestType,
    );
  };

  const handleSiteDeletion = async () => {
    if (!siteDetails?.id) return false;
    const api = await getApi();
    const response = await dispatch(
      deleteSite({
        api,
        value: {
          companyId: siteDetails.companyId,
          siteId: siteDetails.id,
        },
      }),
    );
    if (response.meta.requestStatus === "fulfilled") {
      dispatch(getCompany({ api, value: siteDetails.companyId }));
    }
    return response.meta.requestStatus === "fulfilled";
  };

  const updateSiteField = async (newBodyItem: SiteBody) => {
    if (!siteDetails?.id) return false;
    const siteBody: SiteBody = {
      id: siteDetails.id,
      ...newBodyItem,
    };
    return handleRequests({ requestType: "site", siteBody });
  };

  return (
    <DetailedWrapper
      deleteAction={handleSiteDeletion}
      backLink={backLink}
      detailedPageType="detail"
      detailedPage="site"
    >
      <SubSectionWithStyles title="General settings">
        <SlideOverFieldNamedRow
          onAccept={(value) => updateSiteField({ domain: value })}
          namedRowTitle="Domain"
          permissionsGroup="site:domain"
          slideOverValue={siteDetails?.domain}
        />

        <SlideOverFieldNamedRow
          onAccept={(value) => updateSiteField({ description: value })}
          namedRowTitle="Description"
          permissionsGroup="site:basic_config"
          slideOverValue={siteDetails?.description}
        />

        <Restricted permissions="site:advanced_config:view">
          <NamedSwitch
            title="tracking"
            value={!!trackingConfiguration?.track}
            onChange={(value) =>
              updateSiteField({ trackingConfiguration: trackingConfig(value) })
            }
            permissions="site:advanced_config:update"
            styleVariant="detailPage"
          />
        </Restricted>
      </SubSectionWithStyles>

      <Restricted permissions="site:credentials:view">
        <CredentialsEditBlock
          handleRequests={handleRequests}
          siteDetails={siteDetails}
        />
      </Restricted>

      {siteDetails && (
        <Restricted permissions="site:advanced_config:view">
          <FeedEditBlock
            updateSiteField={updateSiteField}
            config={siteDetails}
            refinementColumns={siteDetails.refinementColumns ?? []}
          />
          <AIMethodsBlock
            handleRequests={handleRequests}
            siteDetails={siteDetails}
          />
          <CDNBlock siteDetails={siteDetails} />
        </Restricted>
      )}
    </DetailedWrapper>
  );
};
